import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { useLayoutEffect, useRef, useState } from 'react'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { getSpotlightSortLabel, SPOTLIGHT_SORT_DESCRIPTIONS, VenueActivityType } from '@ivy-monorepo/shared'
import Icon from '../Icon'

type SpotlightSortOrderTableProps = {
  order: VenueActivityType[]
  onChange: (order: VenueActivityType[]) => void
}

type SortableRowProps = {
  activityKey: VenueActivityType
  position: number
}

const SpotlightSortRowContent = ({
  activityKey,
  position,
  dragHandleProps,
  isOverlay = false,
}: SortableRowProps & {
  dragHandleProps?: Record<string, unknown>
  isOverlay?: boolean
}) => (
  <>
    <td className="map-filter-drag-cell">
      <button
        type="button"
        className="map-filter-drag-handle"
        aria-label={`Drag to reorder ${getSpotlightSortLabel(activityKey)}`}
        disabled={isOverlay}
        {...dragHandleProps}
      >
        <Icon name="GripVertical" className="map-filter-drag-icon" />
        <span className="map-filter-position-label">{position}</span>
      </button>
    </td>
    <td>
      <code>{activityKey}</code>
    </td>
    <td>{getSpotlightSortLabel(activityKey)}</td>
    <td className="feature-flag-subtitle">{SPOTLIGHT_SORT_DESCRIPTIONS[activityKey] ?? ''}</td>
  </>
)

const SortableSpotlightSortRow = ({ activityKey, position }: SortableRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: activityKey,
  })

  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'map-filter-row-dragging' : undefined}
    >
      <SpotlightSortRowContent
        activityKey={activityKey}
        position={position}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </tr>
  )
}

const SpotlightSortOrderTable = ({ order, onChange }: SpotlightSortOrderTableProps) => {
  const tableRef = useRef<HTMLTableElement>(null)
  const [tableWidth, setTableWidth] = useState<number>()
  const [activeKey, setActiveKey] = useState<VenueActivityType | null>(null)

  useLayoutEffect(() => {
    if (!tableRef.current) {
      return
    }

    const updateWidth = () => setTableWidth(tableRef.current?.offsetWidth)
    updateWidth()

    const observer = new ResizeObserver(updateWidth)
    observer.observe(tableRef.current)
    return () => observer.disconnect()
  }, [order.length])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const activePosition = activeKey == null ? -1 : order.indexOf(activeKey)

  const handleDragStart = (event: DragStartEvent) => {
    setActiveKey(event.active.id as VenueActivityType)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveKey(null)

    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = order.indexOf(active.id as VenueActivityType)
    const newIndex = order.indexOf(over.id as VenueActivityType)
    if (oldIndex < 0 || newIndex < 0) {
      return
    }

    onChange(arrayMove(order, oldIndex, newIndex))
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveKey(null)}
    >
      <table ref={tableRef} className="map-filters-table spotlight-sort-table">
        <thead>
          <tr>
            <th className="map-filter-drag-header">Order</th>
            <th>Key</th>
            <th>Label</th>
            <th>Sorts by</th>
          </tr>
        </thead>
        <tbody>
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            {order.map((activityKey, index) => (
              <SortableSpotlightSortRow key={activityKey} activityKey={activityKey} position={index} />
            ))}
          </SortableContext>
        </tbody>
      </table>
      <DragOverlay dropAnimation={null}>
        {activeKey ? (
          <table
            className="map-filters-table map-filters-table-overlay"
            style={tableWidth ? { width: tableWidth } : undefined}
          >
            <tbody>
              <tr className="map-filter-row-overlay">
                <SpotlightSortRowContent activityKey={activeKey} position={activePosition} isOverlay />
              </tr>
            </tbody>
          </table>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export default SpotlightSortOrderTable
